/**
 * OrganizationManagement - Unified organization configuration screen
 *
 * Combines tag management (for all admins) with organization structure management
 * (for OrgAdmins only). Uses collapsible sections for progressive disclosure.
 *
 * Access:
 * - All admins (Analyst role): Tag Management section only
 * - OrgAdmins: Full access including Child Organizations and Routing Configuration
 *
 * Platform behavior:
 * - Web: Full editing capabilities
 * - Mobile: Tag editing + read-only view of org structure and routing
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  KeyboardAvoidingView,
  StyleSheet,
} from 'react-native';
import { isIOS } from '../util/platform';
import { KeyboardAccessoryNavigation } from 'react-native-keyboard-accessory';
import DropDownPicker from 'react-native-dropdown-picker';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';

import { getOrganizationAccess, getIsOrgAdmin, setOrganizationAccess } from '../util/storage';
import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CollapsibleSection from '../components/analytics/CollapsibleSection';
import { TagManagementSection, ChildOrgSection, RoutingMapper, EmailDomainSection, AnalyticsSettingsSection, EscalationSettingsSection, WorkOrderSettingsSection } from '../components/organization';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import Util from '../util/util';
import { useTranslation } from '../i18n';

const OrganizationManagement = (props) => {
  const { t } = useTranslation('admin');

  const { organization, admin, userAdmin, orgAdmin: propOrgAdmin } = props.route.params;

  const { isWeb, isMobile, containerStyle } = useResponsiveStyles();
  // "Mobile" for editing purposes = any narrow viewport, including a phone
  // browser (web platform). Width-based, not platform-based, so
  // web-only org-structure/routing fields stay read-only on mobile devices.

  // Verify OrgAdmin from storage (don't trust props alone for security)
  const [isOrgAdmin, setIsOrgAdmin] = useState(propOrgAdmin === true);

  // Organization picker state
  const [OrgFilterOpen, setOrgFilterOpen] = useState(false);
  const [OrgFilterValue, setOrgFilterValue] = useState(organization);
  const [OrgFilter, setOrgFilter] = useState([]);

  // Section expansion state
  const [tagSectionExpanded, setTagSectionExpanded] = useState(true);
  const [emailDomainSectionExpanded, setEmailDomainSectionExpanded] = useState(false);
  const [childOrgSectionExpanded, setChildOrgSectionExpanded] = useState(false);
  const [routingSectionExpanded, setRoutingSectionExpanded] = useState(false);
  const [analyticsSectionExpanded, setAnalyticsSectionExpanded] = useState(false);
  const [escalationSectionExpanded, setEscalationSectionExpanded] = useState(false);
  const [workOrderSectionExpanded, setWorkOrderSectionExpanded] = useState(false);

  // Key to force refresh RoutingMapper when child orgs change
  const [routingMapperKey, setRoutingMapperKey] = useState(0);

  // Verify OrgAdmin status from storage
  useEffect(() => {
    const verifyOrgAdmin = async () => {
      const storedOrgAdmin = await getIsOrgAdmin();
      setIsOrgAdmin(storedOrgAdmin === true);
    };
    verifyOrgAdmin();
  }, []);

  // Load valid organizations from storage
  const loadOrgs = useCallback(async () => {
    const orgAccess = await getOrganizationAccess();
    setOrgFilter(
      orgAccess.map((x) => ({ label: x, value: x }))
    );
  }, []);

  // Load orgs on mount
  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  // Called when orgs change (create, delete, update) - refresh org access and routing mapper
  const handleOrgChanged = useCallback(async () => {
    // Refresh the user's organization_access from the backend
    try {
      const response = await Util.get('Resty', '/items/getUserRoles');
      if (response.data?.organization_access) {
        await setOrganizationAccess(response.data.organization_access);
        // Reload the org picker with new organizations
        await loadOrgs();
      }
    } catch (err) {
      console.log('Error refreshing org access:', err);
    }
    // Also refresh the routing mapper
    setRoutingMapperKey((prev) => prev + 1);
  }, [loadOrgs]);

  return (
    <View style={containerStyle}>
      <Header organization={organization} admin={admin} userAdmin={userAdmin} />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={isIOS() ? 'padding' : 'height'}
        keyboardVerticalOffset={0}
      >
        <ScrollView
          style={styles.innerContainer}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {/* Page Header */}
          <View style={styles.pageHeader}>
            <CustomText font="semiBold" size={24} color={AppStyles.colorSet.textPrimary}>
              {t('orgSettings.title')}
            </CustomText>
            <CustomText style={styles.pageSubtitle} size={14} color={AppStyles.colorSet.textSecondary}>
              {t('orgSettings.subtitle')}
            </CustomText>
          </View>

          {/* Organization Picker */}
          {OrgFilter && OrgFilter.length > 1 && (
            <View style={styles.pickerContainer}>
              <DropDownPicker
                zIndex={3000}
                zIndexInverse={1000}
                multiple={false}
                min={1}
                open={OrgFilterOpen}
                value={OrgFilterValue}
                items={OrgFilter}
                setOpen={setOrgFilterOpen}
                setValue={setOrgFilterValue}
                setItems={setOrgFilter}
                dropDownDirection="BOTTOM"
                placeholder={t('orgSettings.selectOrganization')}
                maxHeight={158}
                bottomOffset={200}
                style={styles.dropdown}
                dropDownContainerStyle={styles.dropdownContainer}
                textStyle={styles.dropdownText}
                placeholderStyle={styles.dropdownPlaceholder}
                arrowIconStyle={{ tintColor: AppStyles.colorSet.textSecondary }}
              />
            </View>
          )}

          {/* Tag Management Section - Available to all admins */}
          <CollapsibleSection
            title={t('orgSettings.tagManagement')}
            subtitle={tagSectionExpanded ? undefined : t('orgSettings.tagsSubtitle')}
            isExpanded={tagSectionExpanded}
            onToggle={() => setTagSectionExpanded(!tagSectionExpanded)}
            style={styles.section}
          >
            <TagManagementSection
              organization={organization}
              selectedOrg={OrgFilterValue}
            />
          </CollapsibleSection>

          {/* OrgAdmin-only sections */}
          {isOrgAdmin && (
            <>
              {/* Email Domain Restrictions Section */}
              <CollapsibleSection
                title={t('orgSettings.emailDomains')}
                subtitle={emailDomainSectionExpanded ? undefined : t('orgSettings.emailDomainsSubtitle')}
                isExpanded={emailDomainSectionExpanded}
                onToggle={() => setEmailDomainSectionExpanded(!emailDomainSectionExpanded)}
                style={styles.section}
              >
                <EmailDomainSection
                  organization={OrgFilterValue || organization}
                  readOnly={isMobile}
                />
              </CollapsibleSection>

              {/* Child Organizations Section */}
              <CollapsibleSection
                title={t('orgSettings.childOrganizations')}
                subtitle={childOrgSectionExpanded ? undefined : t('orgSettings.childOrganizationsSubtitle')}
                isExpanded={childOrgSectionExpanded}
                onToggle={() => setChildOrgSectionExpanded(!childOrgSectionExpanded)}
                style={styles.section}
              >
                <ChildOrgSection
                  organization={OrgFilterValue || organization}
                  readOnly={isMobile}
                  onOrgChanged={handleOrgChanged}
                />
              </CollapsibleSection>

              {/* Routing Configuration Section */}
              <CollapsibleSection
                title={t('orgSettings.reportRouting')}
                subtitle={routingSectionExpanded ? undefined : t('orgSettings.reportRoutingSubtitle')}
                isExpanded={routingSectionExpanded}
                onToggle={() => setRoutingSectionExpanded(!routingSectionExpanded)}
                style={[styles.section, { zIndex: 10 }]}
              >
                <RoutingMapper
                  key={routingMapperKey}
                  organization={OrgFilterValue || organization}
                  readOnly={isMobile}
                />
              </CollapsibleSection>

              {/* Analytics Settings Section */}
              <CollapsibleSection
                title={t('orgSettings.analyticsSettings')}
                subtitle={analyticsSectionExpanded ? undefined : t('orgSettings.analyticsSettingsSubtitle')}
                isExpanded={analyticsSectionExpanded}
                onToggle={() => setAnalyticsSectionExpanded(!analyticsSectionExpanded)}
                style={[styles.section, { zIndex: 1 }]}
              >
                <AnalyticsSettingsSection
                  organization={OrgFilterValue || organization}
                  readOnly={isMobile}
                />
              </CollapsibleSection>

              {/* Escalation & Reminder Settings Section */}
              <CollapsibleSection
                title={t('orgSettings.ticketEscalation')}
                subtitle={escalationSectionExpanded ? undefined : t('orgSettings.ticketEscalationSubtitle')}
                isExpanded={escalationSectionExpanded}
                onToggle={() => setEscalationSectionExpanded(!escalationSectionExpanded)}
                style={[styles.section, { zIndex: 1 }]}
              >
                <EscalationSettingsSection
                  organization={OrgFilterValue || organization}
                  readOnly={isMobile}
                />
              </CollapsibleSection>

              {/* Work Order Link Section */}
              <CollapsibleSection
                title={t('orgSettings.workOrderLink')}
                subtitle={workOrderSectionExpanded ? undefined : t('orgSettings.workOrderLinkSubtitle')}
                isExpanded={workOrderSectionExpanded}
                onToggle={() => setWorkOrderSectionExpanded(!workOrderSectionExpanded)}
                style={styles.section}
              >
                <WorkOrderSettingsSection
                  organization={OrgFilterValue || organization}
                />
              </CollapsibleSection>
            </>
          )}

          {/* Spacer for scroll padding */}
          <View style={styles.bottomSpacer} />
        </ScrollView>
      </KeyboardAvoidingView>
      <Footer
        admin={admin}
        organization={organization}
        userAdmin={userAdmin}
        orgAdmin={isOrgAdmin}
      />
      {!isWeb && <KeyboardAccessoryNavigation androidAdjustResize avoidKeyboard />}
    </View>
  );
};

const styles = StyleSheet.create({
  innerContainer: {
    backgroundColor: '#F9FAFB',
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  pageHeader: {
    paddingHorizontal: 16,
    paddingTop: hp(2),
    paddingBottom: hp(1),
  },
  pageSubtitle: {
    marginTop: 4,
  },
  pickerContainer: {
    paddingHorizontal: 16,
    marginBottom: hp(2),
    zIndex: 3000,
  },
  dropdown: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderColor: '#E5E5E5',
    borderWidth: 1,
    borderRadius: 12,
    minHeight: 50,
  },
  dropdownContainer: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderColor: '#E5E5E5',
    borderWidth: 1,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  dropdownText: {
    fontFamily: 'Poppins_500Medium',
    color: AppStyles.colorSet.textPrimary,
  },
  dropdownPlaceholder: {
    color: AppStyles.colorSet.textMuted,
  },
  section: {
    marginHorizontal: 4,
  },
  bottomSpacer: {
    height: hp(4),
  },
});

export default OrganizationManagement;
